import { useEffect, useRef } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useLocation,
} from "react-router";
import * as Sentry from "@sentry/react-router";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { createInstance } from "i18next";
import ICU from "i18next-icu";
import "./tailwind.css";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { i18nConfig } from "./i18n/i18n";
import { resolveLocale } from "./i18n/i18next.server";
import { useAuth } from "./lib/auth/useAuth";
import {
  capturePageview,
  identifyUser,
  resetUser,
} from "./lib/analytics/posthog";
import type { Route } from "./+types/root";

export async function loader({ request }: Route.LoaderArgs) {
  const acceptLanguage = request.headers.get("accept-language");
  const locale = resolveLocale(acceptLanguage);
  return { locale };
}

const themeInitScript = `
(function(){
  var s = localStorage.getItem('prefersColorScheme');
  var d = s === 'dark' || (!s && window.matchMedia('(prefers-color-scheme: dark)').matches);
  if (d) document.documentElement.classList.add('dark');
})();
`;

export const Layout = ({ children }: { children: React.ReactNode }) => (
  <html lang="en" suppressHydrationWarning>
    <head>
      <meta charSet="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="icon" href="/icons/favicon.ico" sizes="32x32" />
      <link rel="icon" href="/icons/512.png" type="image/png" sizes="512x512" />
      <link rel="apple-touch-icon" href="/icons/512.png" />
      <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
      <Meta />
      <Links />
    </head>
    <body className="flex min-h-screen flex-col bg-surface-sari font-body text-text-medium antialiased">
      {children}
      <ScrollRestoration />
      <Scripts />
    </body>
  </html>
);

export default function App() {
  const { locale } = useLoaderData<typeof loader>();
  const location = useLocation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const identifiedRef = useRef(false);

  const i18nInstance = createInstance();
  i18nInstance
    .use(ICU)
    .use(initReactI18next)
    .init({
      ...i18nConfig,
      lng: locale,
    });

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    capturePageview();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated && user?.sub) {
      identifyUser(user.sub, user.email);
      identifiedRef.current = true;
    } else if (identifiedRef.current) {
      resetUser();
      identifiedRef.current = false;
    }
  }, [isLoading, isAuthenticated, user?.sub, user?.email]);

  return (
    <I18nextProvider i18n={i18nInstance}>
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </I18nextProvider>
  );
}

function deriveErrorContent(error: unknown): {
  message: string;
  details: string;
} {
  if (isRouteErrorResponse(error)) {
    return error.status === 404
      ? {
          message: "Page not found",
          details: "The requested page could not be found.",
        }
      : {
          message: `Error ${error.status}`,
          details: error.statusText || "An unexpected error occurred.",
        };
  }
  if (error instanceof Error) {
    Sentry.captureException(error);
    return {
      message: "Something went wrong",
      details: import.meta.env.DEV
        ? error.message
        : "An unexpected error occurred.",
    };
  }
  return {
    message: "Something went wrong",
    details: "An unexpected error occurred.",
  };
}

export const ErrorBoundary = ({ error }: Route.ErrorBoundaryProps) => {
  const { message, details } = deriveErrorContent(error);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-sari font-body text-text-medium">
      <h1 className="font-heading text-2xl font-bold text-text-full">
        {message}
      </h1>
      <p className="mt-2">{details}</p>
      <p className="mt-4">
        <a href="/" className="text-brand-primary underline">
          Return home
        </a>
      </p>
    </div>
  );
};
